import hoistStatics from 'hoist-non-react-statics';
import {
  Location,
  NavigateFunction,
  Params,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

export type RouteComponentProps<TParamKey extends string = string> = {
  navigate: NavigateFunction;
  readonly params: Params<TParamKey>;
  location: Location;
};

type WithRouterProps<TComponent extends React.ComponentType<any>> =
  TComponent extends React.ComponentClass
    ? { wrappedComponentRef?: React.Ref<InstanceType<TComponent>> | undefined }
    : Record<string, unknown>;

type WithRouterStatics<TComponent extends React.ComponentType<any>> = {
  WrappedComponent: TComponent;
};

export function withRouter<
  TProps extends RouteComponentProps<any>,
  TComponent extends React.ComponentType<TProps>,
>(
  Component: TComponent & React.ComponentType<TProps>,
): React.ComponentType<
  Omit<TProps, keyof RouteComponentProps<any>> & WithRouterProps<TComponent>
> &
  WithRouterStatics<TComponent> {
  const displayName = `withRouter(${Component.displayName || Component.name})`;

  const C = (props): JSX.Element => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return (
      <Component
        location={location}
        navigate={navigate}
        params={params}
        {...props}
      />
    );
  };

  C.displayName = displayName;
  C.WrappedComponent = Component;

  return hoistStatics(C, Component);
}
